import { Button, message } from 'antd';
import ipaddr from 'ipaddr.js';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import FlowEventModal from './FlowEventModal/index';
import NavigationBar from '@components/navigation-bar';
import { UserTimeline } from '@conviva/organism-components';
import '@conviva/organism-components/styles.css';
import ExplorerIcon from '@icons/explorer.svg';
import {
  useGetExplorerEventList,
  useGetExplorerUsersListByIP,
  useGetUserInfoByUserIdOrClientId,
  usePostSetWhiteList,
} from '@src/emp-3.0/api/queryHooks';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { EQueryIdType } from '@src/emp-3.0/types';
import {
  GET_EXPLORER_EVENT_LIST,
  GET_EXPLORER_USERS_LIST,
  KeyMap,
} from '@src/emp-3.0/utils/constants';
import FindUserByIpModal from '@src/pages/livelens/FindUserByIPModal';
import {
  DATA_STAUS,
  DataStatus,
  useExplorerIdStore,
  useExplorerStore,
} from '@src/pages/livelens/store';

import { Empty } from './Empty';
import {
  EXPLORER_HISTORY_CLIENT_IDS,
  EXPLORER_HISTORY_IPS,
  EXPLORER_HISTORY_USER_IDS,
  useIds,
} from './hooks';
import styles from './index.less';
import {
  generatePayload,
  getPayloadConfig,
  getSummaryInfo,
  storeHistory,
  EventDimensions,
} from './utils';
import ReleaseVersion from '@src/emp-3.0/components/ReleaseVersion';
import {
  trackLeftLiveLens,
  trackBackToLiveLens,
  trackOptionsInLiveLens,
  trackClickClientIdInLiveLens,
  trackGetEventsInLiveLens,
} from '@src/sensor';
import { now } from 'lodash';
import { LiveExplorerContainer, TimelineToolbarTitle } from './styled';
import { useNavigate } from 'react-router-dom';

const GET_EXPLORER_EVENT_LIST_INTERVAL = 1300;
const GET_EXPLORER_USERS_LIST_INTERVAL = 1300;
const GET_EXPLORER_EVENT_LIST_START = { value: 5, unit: 'minutes' };

const CONVIVA_NETWORK_REQUEST = 'conviva_network_request';
const FILTER_EVENT_NAME_DEFAULT = ['conviva_page_ping', 'conviva_periodic_heartbeat'];

const LiveLensPage = ({ pageType }: { pageType?: 'embedded' }) => {
  const [showFindModal, setShowFindModal] = useState(false);
  // const [enableReceive, setEnableReceive] = useState(false);
  const [enableNetworkRequest, setEnableNetworkRequest] = useState(false);
  const { userInfo, workbenchMode } = useInitialInfoStore();
  const { titleValue, selectedId } = useExplorerIdStore();
  const { idGroupData } = useIds();
  const [trimedSelectedId, setTrimSelectedId] = useState<string[]>([]);
  const [isUsersPending, setIsUsersPending] = useState(true);
  const { tzName: timezone } = userInfo;
  // get status of receiving or not of event list from store
  const {
    receivingData,
    explorerFilter,
    setExplorerFilter,
    setReceivingData,
    startTime,
    setStartTime,
    enableReceive,
    setEnableReceive,
    eventCatchData,
    setEventCatchData,
    summaryData,
    setSummaryData,
    stopManual,
    setStopManual,
    autoScroll,
  } = useExplorerStore();
  const eventRef = useRef(eventCatchData);

  const navigate = useNavigate();
  useEffect(() => {
    if (workbenchMode) {
      navigate('/home'); // Redirect to home if workbench mode is enabled
    }
  }, [workbenchMode, navigate]);

  useEffect(() => {
    eventRef.current = eventCatchData;
  }, [eventCatchData]);

  const [userCatchData, setUserCatchData] = useState<any>([]);
  const [isUsersListLoading, setIsUsersListLoading] = useState(false);
  const [eventFilterOn, setEventFilterOn] = React.useState(false);

  const [filterEventNames, setFilterEventNames] = useState<string[]>([
    ...FILTER_EVENT_NAME_DEFAULT,
    CONVIVA_NETWORK_REQUEST,
  ]);

  const { mutate: postSetWhiteList } = usePostSetWhiteList();

  const queryClient = useQueryClient();

  const disabledFind = useMemo(() => {
    if (!Array.isArray(selectedId)) return true;

    const filtered = selectedId.filter((item) => item.trim() !== '');

    return filtered.length === 0;
  }, [selectedId]);

  const generateEventListPayLoad = useMemo(() => {
    if (!startTime) return null;
    const payload = generatePayload(
      getPayloadConfig({
        type: 'EventList',
        field: titleValue,
        value: trimedSelectedId,
        explorerFilter,
        startTime,
      }),
    );
    filterEventNames.forEach((eventName) => {
      const f = [
        {
          display: eventName,
          field: 'event_name',
          key: 'event_name',
          op: 'not eq',
          originalField: 'event_name',
          value: eventName,
          _order: 0,
        },
      ];
      payload.filters.push(f);
    });
    return payload;
    // do not add titleValue as a dependency
  }, [explorerFilter, startTime, filterEventNames]);
  const { data: eventList, isFetching: eventLoading } = useGetExplorerEventList(
    generateEventListPayLoad,
    {
      // tsxlint:disable-next-line
      enabled: !!startTime && !isUsersListLoading && !!explorerFilter.client_id,
      fetching: receivingData === DATA_STAUS.RECEIVE && !showFindModal,
      interval: GET_EXPLORER_EVENT_LIST_INTERVAL,
    },
  );

  const generateExplorerUsersListByIPPayload = useMemo(
    () => {
      const cleanedSelectedId = trimedSelectedId.map((item) => item.replace('(Current)', ''));

      return generatePayload(
        getPayloadConfig({
          type: 'UsersList',
          field: titleValue,
          value: cleanedSelectedId,
        }),
      );
    },
    // do not add titleValue as a dependency
    [trimedSelectedId, titleValue],
  );
  const { data: usersList, isFetching: isUsersListLoading1 } = useGetExplorerUsersListByIP(
    generateExplorerUsersListByIPPayload,
    {
      enabled: !!generateExplorerUsersListByIPPayload && !eventLoading,
      fetching: showFindModal && isUsersPending,
      interval: showFindModal ? GET_EXPLORER_USERS_LIST_INTERVAL : false,
    },
  );
  useEffect(() => {
    setIsUsersListLoading(isUsersListLoading1);
  }, [isUsersListLoading1]);

  const generateGetUserInfo = useMemo(
    () => {
      if (!explorerFilter.user_id && !explorerFilter.client_id) {
        return null;
      }
      return generatePayload(
        getPayloadConfig({
          type: 'UserInfo',
          field: titleValue,
          value: '',
          explorerFilter,
        }),
      );
    },
    // do not add titleValue as a dependency
    [explorerFilter],
  );
  const { data: userSummaryInfo, isFetching: isUserInfoLoading } = useGetUserInfoByUserIdOrClientId(
    generateGetUserInfo,
    {
      enabled: !!generateGetUserInfo && pageType !== 'embedded',
    },
  );

  const appendLastSeenEvent = () => {
    if (eventRef.current.length) {
      const lastSeen = {
        derivedTstamp: moment().format(),
        eventId: 'last-seen',
        eventName: 'Last time seen',
        eventGroupType: 'Nothing',
      };
      const eventListwithoutLastSeen = eventRef.current.filter(
        (event) => event.eventId !== 'last-seen',
      );
      appendNewEvents(eventListwithoutLastSeen || [], [lastSeen]);
    }
  };

  useEffect(() => {
    if (!stopManual) {
      appendLastSeenEvent();
      setReceivingData(DATA_STAUS.RECEIVE);
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackLeftLiveLens({ receivingData });
        if (receivingData === DATA_STAUS.RECEIVE && !stopManual) {
          setReceivingData(DATA_STAUS.STOP);
        }
      } else {
        trackBackToLiveLens({ receivingData });
        if (!stopManual) {
          appendLastSeenEvent();
          setReceivingData(DATA_STAUS.RECEIVE);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setReceivingData(DATA_STAUS.STOP);
    };
  }, []);

  useEffect(() => {
    if (enableNetworkRequest) {
      setFilterEventNames(FILTER_EVENT_NAME_DEFAULT);
    } else {
      setFilterEventNames([...FILTER_EVENT_NAME_DEFAULT, CONVIVA_NETWORK_REQUEST]);
    }
  }, [enableNetworkRequest]);

  useEffect(() => {
    if (pageType === 'embedded') {
      setSummaryData(undefined);
      return;
    }
    if (isUserInfoLoading || !userSummaryInfo) {
      return;
    }
    if (!userSummaryInfo.length) {
      setSummaryData([]);
      return;
    }
    const summary = userSummaryInfo[0];
    setSummaryData(getSummaryInfo(summary));
  }, [userSummaryInfo, setSummaryData, isUserInfoLoading, pageType]);

  const focusFilter = (record) => {
    trackClickClientIdInLiveLens(record);
    const filter = {
      client_id: record.client_id,
      user_id: record.user_id,
      device_name: record.device_name,
      application_version: record.application_version,
    };
    setEventCatchData([]);
    setEventFilterOn(false);
    handleReceive(filter);
    queryClient.cancelQueries(GET_EXPLORER_USERS_LIST);
    setTimeout(() => {
      updateStartTime(GET_EXPLORER_EVENT_LIST_START);
      setShowFindModal(false);
    }, 100);
  };

  const PageTitle = () => (
    <div className={styles.pageTitle}>
      <ExplorerIcon />
      <span>LiveLens</span>
    </div>
  );

  const setWhiteList = (value) => {
    const key = titleValue === EQueryIdType.ip ? 'ip' : titleValue;
    postSetWhiteList({ [key]: value });
  };

  const handleReceive = (filterDraft) => {
    setExplorerFilter({ ...filterDraft });
    setStopManual(false);
    setReceivingData(DATA_STAUS.RECEIVE);
    setEnableReceive(true);
  };

  const updateStartTime = (subtractTime?) => {
    let start = moment().subtract(3, 'seconds').format();
    if (subtractTime) {
      start = moment().subtract(subtractTime.value, subtractTime.unit).format();
    }
    setStartTime(start);
  };
  const handleFind = () => {
    const value = selectedId.map((item) => item.trim().replace('(Current)', ''));

    if (titleValue === EQueryIdType.ip) {
      // ipaddr thinks a number within the range of IPv4 is a valid IPv4 address 0-2^32-1
      const ipValues = value;
      const invalidIp = ipValues.find((ip) => /^\d+$/.exec(ip) || !ipaddr.isValid(ip));
      if (invalidIp) {
        message.error('Invalid IP address');
        return;
      }
      storeHistory(EXPLORER_HISTORY_IPS, ipValues);
    }
    setTrimSelectedId(value);
    if (titleValue === EQueryIdType.userId) {
      storeHistory(EXPLORER_HISTORY_USER_IDS, value);
    }
    if (titleValue === EQueryIdType.clientId) {
      storeHistory(EXPLORER_HISTORY_CLIENT_IDS, value);
    }
    setWhiteList(value);
    setUserCatchData([]);
    setIsUsersPending(true);
    setTimeout(() => {
      queryClient.cancelQueries(GET_EXPLORER_EVENT_LIST);
      setShowFindModal(true);
    }, 100);
  };

  const timelineData = useMemo(() => {
    const res = eventList?.map((event) => {
      const customTags = {};
      const eventTags = {};
      const dimensions = {};
      const customTagsData = event.custom_tags || {};
      Object.keys(event).forEach((x) => {
        if (EventDimensions.includes(x)) {
          dimensions[`metadata:${x}`] = event[x];
        }
      });
      Object.keys(customTagsData).forEach((x) => {
        customTags[`custom_tag:${x}`] = customTagsData[x];
      });
      Object.keys(event.event_tags || {}).forEach((x) => {
        if (KeyMap[x]) {
          eventTags[KeyMap[x]] = event.event_tags[x];
        } else {
          eventTags[x] = event.event_tags[x];
        }
      });

      const eventAttributes = {
        ...customTags,
        ...eventTags,
        ...dimensions,
      };
      return {
        derivedTstamp: event.event_time,
        sessionId: event.session_id,
        inSession: event.in_session,
        eventName: event.event_name,
        eventAttributes,
        eventId: event.event_id,
        eventGroupType: 'Unmapped Event',
        ...(pageType === 'embedded' && {
          floatContent: (
            <FlowEventModal
              event={{
                eventName: event.event_name,
                eventAttributes: eventAttributes,
              }}
            />
          ),
        }),
      };
    });
    return res;
  }, [eventList]);

  const appendNewEvents = React.useCallback((oldData, newData) => {
    if (!newData.length) return;

    try {
      const lastTimeSeenIndex = oldData.findIndex((x) => x.eventId === 'last-seen');
      const lastTimeSeenEvent = oldData[lastTimeSeenIndex];
      const lastEvent = lastTimeSeenIndex > -1 ? oldData[lastTimeSeenIndex + 1] : null;
      const pureOldDataEvent = oldData.filter(
        (x) => !newData.find((y) => y.eventId === x.eventId) && x.eventId !== 'last-seen',
      );

      const timeLineEvents = [...newData, ...pureOldDataEvent.slice(0, 500)];
      if (lastEvent && lastTimeSeenEvent) {
        const lastEventIndex = timeLineEvents.findIndex((x) => x.eventId === lastEvent.eventId);
        timeLineEvents.splice(lastEventIndex, 0, lastTimeSeenEvent);
      }
      setEventCatchData(timeLineEvents);
    } catch (error) {
      console.error('Error appending new events:', error);
    }
  }, []);

  useEffect(() => {
    if (!eventLoading && timelineData) {
      if (timelineData.length > 0 && eventCatchData.length === 0) {
        trackGetEventsInLiveLens({
          startTime,
          now: now(),
          count: timelineData.length,
          firstEvent: timelineData[0],
        });
      }
      appendNewEvents(eventRef.current || [], timelineData);
    }
  }, [timelineData, eventLoading, appendNewEvents]);

  useEffect(() => {
    if (!isUsersListLoading && usersList && showFindModal) {
      setUserCatchData(usersList);
    }
  }, [usersList, isUsersListLoading, showFindModal]);

  const timelineConfig = useMemo(() => {
    if (receivingData === DATA_STAUS.STOP && !eventCatchData?.length) {
      return {
        itemRenderers: {
          'empty-view': () => <Empty />,
        },
      };
    }
    return undefined;
  }, [receivingData, eventCatchData]);

  const TimelineTitle = () => {
    return (
      <span
        style={{
          maxWidth: 'calc(100% - 125px)',
          minWidth: '200px',
        }}
      >
        <span style={{ fontWeight: 500 }}>Real-time Timeline: </span>
        {explorerFilter.client_id && (
          <span
            style={{
              fontWeight: 'normal',
              display: 'inline-block',
              maxWidth: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              verticalAlign: 'bottom',
            }}
          >{`${explorerFilter.client_id}(Client ID)`}</span>
        )}
      </span>
    );
  };
  const FindComponent = () => {
    return (
      <div style={{ display: 'flex', flex: 1, marginLeft: '16px', marginRight: '60px' }}>
        <Button type="primary" onClick={handleFind} disabled={disabledFind}>
          Find
        </Button>
        <div style={{ flex: 1, textAlign: 'right' }}></div>
      </div>
    );
  };
  const toolbarText = ' ';
  const timelineToolbarTitle = (
    <TimelineToolbarTitle>
      <span title={toolbarText}>{toolbarText}</span>
    </TimelineToolbarTitle>
  );

  return (
    <LiveExplorerContainer embedded={pageType === 'embedded'}>
      {pageType !== 'embedded' ? (
        <>
          <NavigationBar />
          <ReleaseVersion type={'Beta'} style={{ insetInlineEnd: 0, top: 0, zIndex: 999 }} />
        </>
      ) : null}
      <UserTimeline
        timezone={timezone || 'Etc/GMT'}
        summaryData={summaryData}
        timelineData={eventCatchData}
        idGroupData={idGroupData}
        autoScroll={autoScroll}
        pageTitle={<PageTitle />}
        headerRightComp={<FindComponent />}
        timelineTitle={<TimelineTitle />}
        timelineToolbarTitle={timelineToolbarTitle}
        timelineConfig={timelineConfig}
        showDataStatusBtn={enableReceive}
        dataStatus={receivingData}
        eventFilterOn={eventFilterOn}
        onEventFilterOnChange={(value) => {
          setEventFilterOn(value);
        }}
        onDataStatusChange={(value) => {
          trackOptionsInLiveLens({
            option: 'Change Status',
            startTime,
            now: now(),
            status: value,
          });
          if (value === DATA_STAUS.RECEIVE) {
            appendLastSeenEvent();
            updateStartTime(GET_EXPLORER_EVENT_LIST_START);
            setStopManual(false);
          } else {
            setStopManual(true);
          }
          setReceivingData(value as DataStatus);
        }}
        clearTimeline={() => {
          trackOptionsInLiveLens({ option: 'Clear', startTime, now: now() });
          setEventCatchData([]);
          updateStartTime();
        }}
        enableShowSummary={false}
        showClearBtn
        showAutoScrollBtn
        enableNetworkRequest={enableNetworkRequest}
        onNetworkRequestToggleChange={() => {
          trackOptionsInLiveLens({
            option: 'Toggle NetworkRequest',
            startTime,
            now: now(),
            enableNetworkRequest: !enableNetworkRequest,
          });
          setEnableNetworkRequest(!enableNetworkRequest);
          updateStartTime();
        }}
      />
      <FindUserByIpModal
        open={showFindModal}
        data={userCatchData}
        isLoading={isUsersListLoading}
        onClickId={focusFilter}
        onCancel={() => setShowFindModal(false)}
        afterClose={() => setShowFindModal(false)}
        titleValue={titleValue}
        isPending={isUsersPending}
        onIsPendingChange={(value) => {
          setIsUsersPending(value);
        }}
      />
    </LiveExplorerContainer>
  );
};

export default LiveLensPage;
